import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  AlertTriangle,
  Building2,
  CheckCircle2,
  Clock3,
  Database,
  FileText,
  Info,
  Link2,
  RefreshCw,
  ShieldCheck,
  Zap,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { masterSupabase } from '../lib/supabase-master';
import { useAuth } from '../contexts/AuthContext';
import { capitalFetch } from '../services/capitalApiService.js';

const CAPITAL_API = 'https://clara360.de/api/capital/trpc';

const scopeLabels = {
  general_supply: 'Allgemeinstrom',
  common_area: 'Gemeinschaftsfläche',
  elevator: 'Aufzug',
  individual_supply_point: 'Einzelverbrauchsstelle',
  contract_scope_unknown: 'Verbrauchsstelle noch prüfen',
};

const deadlinePresentation = {
  deadline_unknown: { label: 'Frist unbekannt', className: 'bg-amber-50 text-amber-800 border-amber-200', Icon: AlertTriangle },
  unlimited_verified: { label: 'Unbefristet belegt', className: 'bg-emerald-50 text-emerald-800 border-emerald-200', Icon: ShieldCheck },
  deadline_passed: { label: 'Frist überschritten', className: 'bg-red-50 text-red-800 border-red-200', Icon: AlertTriangle },
  in_30_days: { label: 'Frist ≤ 30 Tage', className: 'bg-red-50 text-red-800 border-red-200', Icon: Clock3 },
  in_60_days: { label: 'Frist ≤ 60 Tage', className: 'bg-orange-50 text-orange-800 border-orange-200', Icon: Clock3 },
  in_90_days: { label: 'Frist ≤ 90 Tage', className: 'bg-amber-50 text-amber-800 border-amber-200', Icon: Clock3 },
  in_120_days: { label: 'Frist ≤ 120 Tage', className: 'bg-blue-50 text-blue-800 border-blue-200', Icon: Clock3 },
  monitored: { label: 'Frist überwacht', className: 'bg-emerald-50 text-emerald-800 border-emerald-200', Icon: CheckCircle2 },
};

const quarterlyReviewPresentation = {
  data_quality_review_required: { label: 'Quartalsprüfung: Daten prüfen', className: 'bg-amber-50 text-amber-800 border-amber-200', Icon: AlertTriangle },
  comparison_preparation_candidate: { label: 'Quartalsprüfung: Vergleich vorbereitbar', className: 'bg-blue-50 text-blue-800 border-blue-200', Icon: Clock3 },
};

// recommendation_state: kontextabhängige Auskunft statt „Tarife vergleichen“.
// Reine Statusanzeige – kein Button, keine Anbieteranfrage, keine Ausführung.
const recommendationPresentation = {
  SAVE: { label: 'Einsparpotenzial belegt – Angebote ansehen', className: 'bg-emerald-50 text-emerald-800 border-emerald-200', Icon: CheckCircle2 },
  KEEP: { label: 'Kein belegter Vorteil – Vertrag beibehalten', className: 'bg-sky-50 text-sky-800 border-sky-200', Icon: ShieldCheck },
  REVIEW: { label: 'Zuordnung/Scope prüfen', className: 'bg-amber-50 text-amber-800 border-amber-200', Icon: Link2 },
  INSUFFICIENT: { label: 'Marktbenchmark ausstehend – keine Empfehlung', className: 'bg-slate-100 text-slate-700 border-slate-200', Icon: Database },
};

function recommendationLabel(state, savingsEur, deltaYear1Eur) {
  const base = recommendationPresentation[state] || recommendationPresentation.INSUFFICIENT;
  if (state === 'SAVE') {
    const delta = typeof savingsEur === 'number' ? savingsEur : deltaYear1Eur;
    if (typeof delta === 'number') return `${formatEuro(delta)} Einsparpotenzial – Angebote ansehen`;
  }
  return base.label;
}

function capitalInput(endpoint, input) {
  return `${CAPITAL_API}/${endpoint}?batch=1&input=${encodeURIComponent(JSON.stringify({ '0': { json: input } }))}`;
}

function capitalMutationInput(input) {
  return JSON.stringify({ '0': { json: input } });
}

function formatEuro(value) {
  if (typeof value !== 'number' || !Number.isFinite(value)) return null;
  return new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' }).format(value);
}

function formatNumber(value, unit = '') {
  if (typeof value !== 'number' || !Number.isFinite(value)) return null;
  return `${new Intl.NumberFormat('de-DE', { maximumFractionDigits: 1 }).format(value)}${unit ? ` ${unit}` : ''}`;
}

function qualityPresentation(record) {
  if (record?.evidence?.assignmentStatus === 'conflict') {
    return { label: 'Konfliktquelle', className: 'bg-red-50 text-red-800 border-red-200', Icon: AlertTriangle };
  }
  if (record?.evidence?.assignmentStatus === 'assignment_pending') {
    return { label: 'Zuordnung offen', className: 'bg-slate-100 text-slate-700 border-slate-200', Icon: Link2 };
  }
  if (record?.evidence?.qualityStatus === 'verified') {
    return { label: 'Primärbeleg verifiziert', className: 'bg-emerald-50 text-emerald-800 border-emerald-200', Icon: ShieldCheck };
  }
  if (record?.evidence?.qualityStatus === 'partial') {
    return { label: 'Primärbeleg teilweise belegt', className: 'bg-amber-50 text-amber-800 border-amber-200', Icon: ShieldCheck };
  }
  if (record?.contract?.validationStatus === 'validated') {
    return { label: 'Contract-Kandidat geprüft', className: 'bg-violet-50 text-violet-800 border-violet-200', Icon: CheckCircle2 };
  }
  return { label: 'Datenlücke', className: 'bg-slate-100 text-slate-700 border-slate-200', Icon: Database };
}

function compactPeriod(evidence) {
  if (!evidence?.billingPeriodStart || !evidence?.billingPeriodEnd) return null;
  const start = new Date(`${evidence.billingPeriodStart}T00:00:00`).toLocaleDateString('de-DE');
  const end = new Date(`${evidence.billingPeriodEnd}T00:00:00`).toLocaleDateString('de-DE');
  return `${start}–${end}`;
}

export default function EnergyContractHubPage() {
  const { organizationId } = useAuth();
  const [hub, setHub] = useState(null);
  const [owners, setOwners] = useState([]);
  const [properties, setProperties] = useState([]);
  const [selectedOwner, setSelectedOwner] = useState('all');
  const [selectedProperty, setSelectedProperty] = useState('all');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [notificationSettings, setNotificationSettings] = useState(null);
  const [notificationSaving, setNotificationSaving] = useState(false);
  const [notificationError, setNotificationError] = useState(null);

  const loadHierarchy = useCallback(async () => {
    try {
      const [{ data: ownersData }, { data: propertiesData }] = await Promise.all([
        masterSupabase.from('owners').select('id, name').order('name'),
        masterSupabase.from('properties').select('id, property_name, owner_id').order('property_name'),
      ]);
      setOwners(ownersData || []);
      setProperties(propertiesData || []);
    } catch (loadError) {
      console.warn('[EnergyContractHub] owner/property hierarchy unavailable', loadError);
    }
  }, []);

  const loadHub = useCallback(async () => {
    if (!organizationId) {
      setHub(null);
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const response = await capitalFetch(capitalInput('energyContractHub.byOrganization', { organizationId }));
      if (!response.ok) {
        throw new Error(response.status === 403 ? 'Für den Stromvertrags-Hub fehlt eine berechtigte Organisationszuordnung.' : `Hub konnte nicht geladen werden (${response.status}).`);
      }
      const raw = await response.json();
      const data = raw?.[0]?.result?.data?.json;
      if (!data || data.readOnly !== true) throw new Error('Der Hub hat keine gültige Read-only-Antwort geliefert.');
      setHub(data);
    } catch (loadError) {
      console.error('[EnergyContractHub] load failed', loadError);
      setHub(null);
      setError(loadError?.message || 'Der Hub konnte nicht geladen werden.');
    } finally {
      setLoading(false);
    }
  }, [organizationId]);

  useEffect(() => {
    loadHierarchy();
  }, [loadHierarchy]);

  useEffect(() => {
    loadHub();
  }, [loadHub]);

  const loadNotificationSettings = useCallback(async () => {
    if (!organizationId) return;
    try {
      const response = await capitalFetch(capitalInput('energyNotifications.byOrganization', { organizationId }), { organizationId });
      if (!response.ok) return;
      const raw = await response.json();
      const data = raw?.[0]?.result?.data?.json;
      if (data?.channel === 'email' && data?.productionDelivery === 'disabled') setNotificationSettings(data);
    } catch (loadError) {
      console.warn('[EnergyContractHub] notification settings unavailable', loadError);
    }
  }, [organizationId]);

  useEffect(() => {
    loadNotificationSettings();
  }, [loadNotificationSettings]);

  const updateNotificationPreference = useCallback(async (key, enabled) => {
    if (!notificationSettings || !organizationId || notificationSaving) return;
    const preferences = { ...notificationSettings.preferences, [key]: enabled };
    setNotificationSaving(true);
    setNotificationError(null);
    try {
      const response = await capitalFetch(`${CAPITAL_API}/energyNotifications.setMyEmailPreferences?batch=1`, {
        method: 'POST',
        organizationId,
        headers: { 'Content-Type': 'application/json' },
        body: capitalMutationInput({ organizationId, preferences }),
      });
      if (!response.ok) throw new Error(`Benachrichtigungseinstellung konnte nicht gespeichert werden (${response.status}).`);
      const raw = await response.json();
      const data = raw?.[0]?.result?.data?.json;
      if (!data?.updated || data?.productionDelivery !== 'disabled') throw new Error('Der Notification-Service hat keine sichere Bestätigung geliefert.');
      setNotificationSettings((current) => ({ ...current, preferences: data.preferences }));
    } catch (saveError) {
      setNotificationError(saveError?.message || 'Benachrichtigungseinstellung konnte nicht gespeichert werden.');
    } finally {
      setNotificationSaving(false);
    }
  }, [notificationSaving, notificationSettings, organizationId]);

  const availableProperties = useMemo(() => {
    if (selectedOwner === 'all') return properties;
    return properties.filter((property) => property.owner_id === selectedOwner);
  }, [properties, selectedOwner]);

  useEffect(() => {
    if (selectedProperty !== 'all' && !availableProperties.some((property) => property.id === selectedProperty)) {
      setSelectedProperty('all');
    }
  }, [availableProperties, selectedProperty]);

  const propertyNames = useMemo(() => new Map(properties.map((property) => [property.id, property.property_name])), [properties]);
  const propertyOwners = useMemo(() => new Map(properties.map((property) => [property.id, property.owner_id])), [properties]);

  const visibleRecords = useMemo(() => {
    const all = hub?.records || [];
    return all.filter((record) => {
      if (selectedProperty !== 'all' && record.propertyId !== selectedProperty) return false;
      if (selectedOwner !== 'all' && propertyOwners.get(record.propertyId) !== selectedOwner) return false;
      return true;
    });
  }, [hub, propertyOwners, selectedOwner, selectedProperty]);

  const summary = useMemo(() => ({
    records: visibleRecords.length,
    unlimitedVerified: visibleRecords.filter((record) => record.deadlineStatus === 'unlimited_verified').length,
    deadlineUnknown: visibleRecords.filter((record) => record.deadlineStatus === 'deadline_unknown').length,
    assignmentPending: visibleRecords.filter((record) => record.evidence?.assignmentStatus === 'assignment_pending').length,
    quarterlyReviewOpen: visibleRecords.filter((record) => record.quarterlyReview).length,
  }), [visibleRecords]);

  const selectedOwnerName = owners.find((owner) => owner.id === selectedOwner)?.name || 'Alle Eigentümer';
  const selectedPropertyName = properties.find((property) => property.id === selectedProperty)?.property_name || 'Alle Objekte';

  return (
    <div className="space-y-6 pb-10">
      <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
        <div>
          <div className="flex items-center gap-2 text-sm font-medium text-violet-700 dark:text-violet-300">
            <Zap className="h-4 w-4" />
            Energie · Monitoring Hub V1
          </div>
          <h1 className="mt-1 text-3xl font-bold tracking-tight text-slate-950 dark:text-white">Stromvertrags-Hub</h1>
          <p className="mt-2 max-w-3xl text-sm leading-6 text-slate-600 dark:text-slate-300">
            Evidenzgebundene Übersicht pro Objekt und Verbrauchsstelle. Der Hub trennt belegte Daten von Datenlücken und führt keine Vertragsausführung durch.
          </p>
        </div>
        <Badge variant="outline" className="w-fit gap-1.5 border-violet-200 bg-violet-50 px-3 py-1.5 text-violet-800 dark:border-violet-700 dark:bg-violet-950/40 dark:text-violet-200">
          <ShieldCheck className="h-4 w-4" /> Nur Lesen
        </Badge>
      </div>

      <div className="flex gap-3 rounded-xl border border-violet-200 bg-violet-50/80 px-4 py-3 text-sm text-violet-950 dark:border-violet-800 dark:bg-violet-950/40 dark:text-violet-100">
        <Info className="mt-0.5 h-4 w-4 shrink-0" />
        <p>
          Der Hub überwacht ausschließlich Evidenz, Fristen und quartalsweise Datenprüfungen. <strong>Kündigung, Verlängerung, Tarifvergleich, Provideranfrage und Partneraktion sind nicht aktiviert.</strong>
        </p>
      </div>

      <Card className="border-slate-200 shadow-sm dark:border-slate-800">
        <CardContent className="flex flex-col gap-4 p-4 sm:flex-row sm:items-end">
          <label className="grid flex-1 gap-1.5 text-sm font-medium text-slate-700 dark:text-slate-200">
            Eigentümer
            <select value={selectedOwner} onChange={(event) => setSelectedOwner(event.target.value)} className="h-10 rounded-md border border-slate-200 bg-white px-3 text-sm font-normal text-slate-900 outline-none ring-blue-500 focus:ring-2 dark:border-slate-700 dark:bg-slate-950 dark:text-white">
              <option value="all">Alle Eigentümer</option>
              {owners.map((owner) => <option key={owner.id} value={owner.id}>{owner.name}</option>)}
            </select>
          </label>
          <label className="grid flex-1 gap-1.5 text-sm font-medium text-slate-700 dark:text-slate-200">
            Objekt
            <select value={selectedProperty} onChange={(event) => setSelectedProperty(event.target.value)} className="h-10 rounded-md border border-slate-200 bg-white px-3 text-sm font-normal text-slate-900 outline-none ring-blue-500 focus:ring-2 dark:border-slate-700 dark:bg-slate-950 dark:text-white">
              <option value="all">Alle Objekte</option>
              {availableProperties.map((property) => <option key={property.id} value={property.id}>{property.property_name}</option>)}
            </select>
          </label>
          <Button variant="outline" onClick={loadHub} disabled={loading} className="gap-2">
            <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} /> Aktualisieren
          </Button>
        </CardContent>
      </Card>

      {notificationSettings && <Card className="border-sky-200 bg-sky-50/50 shadow-sm dark:border-sky-900 dark:bg-sky-950/20">
        <CardHeader className="pb-3"><CardTitle className="flex items-center gap-2 text-base text-slate-900 dark:text-white"><Info className="h-4 w-4 text-sky-700 dark:text-sky-300" /> E-Mail-Hinweise vorbereiten</CardTitle></CardHeader>
        <CardContent className="space-y-4">
          <p className="text-sm leading-6 text-slate-700 dark:text-slate-200">Du bestimmst, welche belegten Stromvertrags-Hinweise später für deine Organisation in die E-Mail-Outbox dürfen. <strong>Der Versand bleibt in diesem Release deaktiviert</strong>; es gibt keinen CHECK24-/Verivox-Aufruf, keine Angebotsabfrage und keine Vertragsausführung.</p>
          <div className="grid gap-3 md:grid-cols-3">
            <NotificationToggle label="Frist prüfen" detail="Belegte Kündigungsfrist erreicht ein Fristenfenster." checked={notificationSettings.preferences.deadlineReview} disabled={notificationSaving} onCheckedChange={(value) => updateNotificationPreference('deadlineReview', value)} />
            <NotificationToggle label="Daten ergänzen" detail="Quartalsprüfung erkennt eine belastbare Datenlücke." checked={notificationSettings.preferences.dataQualityReview} disabled={notificationSaving} onCheckedChange={(value) => updateNotificationPreference('dataQualityReview', value)} />
            <NotificationToggle label="Vergleich vorbereitbar" detail="Nur interne Datenreife; keine Tarif- oder Anbieteranfrage." checked={notificationSettings.preferences.comparisonReady} disabled={notificationSaving} onCheckedChange={(value) => updateNotificationPreference('comparisonReady', value)} />
          </div>
          {notificationError && <p className="text-sm text-red-700 dark:text-red-300">{notificationError}</p>}
          <p className="text-xs text-slate-500">Outbox: {notificationSettings.summary.pending} wartend · {notificationSettings.summary.dryRun} Dry-Run · {notificationSettings.summary.sent} versandt. Aktuell ist der Live-Versand fest gesperrt.</p>
        </CardContent>
      </Card>}

      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-5">
        <MetricCard icon={FileText} value={summary.records} label="Hub-Zeilen" tone="slate" />
        <MetricCard icon={ShieldCheck} value={summary.unlimitedVerified} label="Unbefristet belegt" tone="emerald" />
        <MetricCard icon={AlertTriangle} value={summary.deadlineUnknown} label="Friststatus offen" tone="amber" />
        <MetricCard icon={Link2} value={summary.assignmentPending} label="Zuordnung offen" tone="blue" />
        <MetricCard icon={Clock3} value={summary.quarterlyReviewOpen} label="Quartalsprüfung offen" tone="blue" />
      </div>

      <Card className="overflow-hidden border-slate-200 shadow-sm dark:border-slate-800">
        <CardHeader className="border-b border-slate-100 pb-4 dark:border-slate-800">
          <CardTitle className="flex items-center gap-2 text-base"><Building2 className="h-4 w-4 text-violet-600 dark:text-violet-300" /> {selectedOwnerName} · {selectedPropertyName}</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex min-h-56 items-center justify-center gap-3 text-sm text-slate-500"><RefreshCw className="h-4 w-4 animate-spin" /> Stromvertragsdaten werden geladen …</div>
          ) : error ? (
            <div className="m-5 rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-900 dark:border-red-900 dark:bg-red-950/40 dark:text-red-100">{error}</div>
          ) : visibleRecords.length === 0 ? (
            <div className="flex min-h-56 flex-col items-center justify-center gap-2 p-8 text-center"><Database className="h-7 w-7 text-slate-400" /><p className="font-medium text-slate-700 dark:text-slate-200">Keine kanonischen Stromvertrags- oder Evidence-Daten im gewählten Umfang.</p><p className="max-w-xl text-sm text-slate-500">Das ist kein Hinweis auf einen fehlenden Vertrag. Der Hub zeigt nur vorhandene kanonische Records und keine aus Suchtreffern abgeleiteten Verträge.</p></div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full min-w-[1200px] text-left text-sm">
                <thead className="bg-slate-50 text-xs font-semibold uppercase tracking-wide text-slate-500 dark:bg-slate-900/70 dark:text-slate-400">
                  <tr>
                    <th className="px-5 py-3">Objekt & Verbrauchsstelle</th>
                    <th className="px-5 py-3">Anbieter / Tarif</th>
                    <th className="px-5 py-3">Evidenz</th>
                    <th className="px-5 py-3">Laufzeit &amp; Frist</th>
                    <th className="px-5 py-3">Vergleichsstatus</th>
                    <th className="px-5 py-3">Quartalsprüfung</th>
                    <th className="px-5 py-3">Nächste interne Aktion</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                  {visibleRecords.map((record) => <HubRow key={record.id} record={record} propertyName={propertyNames.get(record.propertyId) || record.propertyId} />)}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      <p className="text-xs leading-5 text-slate-500">
        Legende: <span className="font-medium text-emerald-700">verifiziert</span> = belastbare Primärevidence; <span className="font-medium text-amber-700">Datenlücke</span> = vor einer Entscheidung zu prüfen; <span className="font-medium text-slate-700">nicht ausführen</span> = keine automatische Vertrags- oder Provideraktion.
      </p>
    </div>
  );
}

function MetricCard({ icon: Icon, value, label, tone }) {
  const tones = {
    slate: 'text-slate-700 bg-slate-50 border-slate-200 dark:text-slate-200 dark:bg-slate-900 dark:border-slate-800',
    emerald: 'text-emerald-700 bg-emerald-50 border-emerald-100 dark:text-emerald-200 dark:bg-emerald-950/30 dark:border-emerald-900',
    amber: 'text-amber-700 bg-amber-50 border-amber-100 dark:text-amber-200 dark:bg-amber-950/30 dark:border-amber-900',
    blue: 'text-violet-700 bg-violet-50 border-violet-100 dark:text-violet-200 dark:bg-violet-950/30 dark:border-violet-900',
  };
  return <Card className={tones[tone]}><CardContent className="flex items-center justify-between p-5"><div><p className="text-3xl font-bold">{value}</p><p className="mt-1 text-sm font-medium opacity-90">{label}</p></div><Icon className="h-6 w-6 opacity-80" /></CardContent></Card>;
}

function NotificationToggle({ label, detail, checked, disabled, onCheckedChange }) {
  return <div className="flex items-start justify-between gap-3 rounded-lg border border-sky-100 bg-white p-4 dark:border-sky-900 dark:bg-slate-950"><div><p className="font-medium text-slate-900 dark:text-white">{label}</p><p className="mt-1 text-xs leading-5 text-slate-600 dark:text-slate-300">{detail}</p></div><Switch checked={checked} disabled={disabled} onCheckedChange={onCheckedChange} aria-label={label} /></div>;
}

function HubRow({ record, propertyName }) {
  const quality = qualityPresentation(record);
  const deadline = deadlinePresentation[record.deadlineStatus] || deadlinePresentation.deadline_unknown;
  const quarterlyReview = record.quarterlyReview;
  const quarterly = quarterlyReview ? (quarterlyReviewPresentation[quarterlyReview.kind] || quarterlyReviewPresentation.data_quality_review_required) : null;
  const QualityIcon = quality.Icon;
  const DeadlineIcon = deadline.Icon;
  const QuarterlyIcon = quarterly?.Icon;
  const recommendation = record.recommendation;
  const signal = recommendation?.signal;
  const baselines = recommendation?.baselines;
  const marketBest = baselines?.marketBest;
  const recPresentation = recommendationPresentation[recommendation?.recommendationState] || recommendationPresentation.INSUFFICIENT;
  const RecIcon = recPresentation.Icon;
  const evidence = record.evidence;
  const termEvidence = record.termEvidence;
  const facts = [
    formatNumber(evidence?.electricityConsumptionKwh, 'kWh'),
    formatEuro(evidence?.electricityCostGrossEur),
    compactPeriod(evidence),
  ].filter(Boolean);
  const provider = record.providerName || 'Anbieter noch nicht belegt';
  const tariff = record.tariffName ? `${record.tariffName}${record.tariffStatus ? ` · ${record.tariffStatus}` : ''}` : record.contract?.contractNumber ? `Vertragsnr. ${record.contract.contractNumber}` : 'Tarif noch nicht belegt';

  return (
    <tr className={record.rowKind === 'evidence' ? 'bg-blue-50/30 dark:bg-blue-950/10' : 'bg-white dark:bg-slate-950'}>
      <td className="px-5 py-4 align-top"><div className="font-semibold text-slate-900 dark:text-white">{propertyName}</div><div className="mt-1 text-xs text-slate-500">{scopeLabels[record.supplyScope] || record.supplyScope}</div>{record.rowKind === 'contract' && <div className="mt-1 text-xs text-slate-500">Contract-Kandidat · {record.contract?.validationStatus || 'Status unbekannt'}</div>}</td>
      <td className="px-5 py-4 align-top"><div className="font-medium text-slate-900 dark:text-white">{provider}</div><div className="mt-1 text-xs text-slate-500">{tariff}</div></td>
      <td className="px-5 py-4 align-top"><Badge variant="outline" className={`gap-1 ${quality.className}`}><QualityIcon className="h-3.5 w-3.5" />{quality.label}</Badge>{facts.length > 0 && <div className="mt-2 max-w-[260px] text-xs leading-5 text-slate-600 dark:text-slate-300">{facts.join(' · ')}</div>}{evidence?.meterReadingQuality === 'partially_estimated' && <div className="mt-1 text-xs text-amber-700 dark:text-amber-300">Zählerstände teilweise geschätzt</div>}{evidence?.sourceDocumentId && <div className="mt-2 text-xs font-medium text-violet-700 dark:text-violet-300">Quelle: Vault {evidence.sourceDocumentId}</div>}</td>
     <td className="px-5 py-4 align-top"><Badge variant="outline" className={`gap-1 ${deadline.className}`}><DeadlineIcon className="h-3.5 w-3.5" />{deadline.label}</Badge>{termEvidence && <div className="mt-2 max-w-[250px] text-xs leading-5 text-slate-600 dark:text-slate-300"><div className="font-medium text-slate-800 dark:text-slate-100">{termEvidence.supplyRegime} · {termEvidence.contractTerm}</div><div>Gesetzliche Kündigungsfrist: {termEvidence.noticePeriodDays} Tage</div><div className="mt-1 text-slate-500">Laut Primärrechnung; aktueller Lieferstatus noch bestätigen.</div></div>}{record.deadlineDate && <div className="mt-2 text-xs text-slate-500">{new Date(`${record.deadlineDate}T00:00:00`).toLocaleDateString('de-DE')}{typeof record.daysUntilDeadline === 'number' ? ` · ${record.daysUntilDeadline} Tage` : ''}</div>}</td>
      <td className="px-5 py-4 align-top">
        <Badge variant="outline" className={`gap-1 ${recPresentation.className}`}><RecIcon className="h-3.5 w-3.5" />{recommendationLabel(recommendation?.recommendationState, recommendation?.savingsEur, recommendation?.deltaYear1Eur)}</Badge>
        {signal?.type === 'DEFAULT_SUPPLY' && <div className="mt-2"><Badge variant="outline" className="gap-1 bg-orange-50 text-orange-800 border-orange-200"><AlertTriangle className="h-3.5 w-3.5" />Grundversorgung · Optimierungssignal {signal.priority}</Badge></div>}
        {baselines?.currentContractAnnualCostEur != null ? (
          <div className="mt-2 max-w-[250px] text-xs leading-5 text-slate-600 dark:text-slate-300">
            <div className="font-medium text-slate-800 dark:text-slate-100">Heute: {formatEuro(baselines.currentContractAnnualCostEur)} / Jahr{recommendation?.annualConsumptionKwh ? ` · ${formatNumber(recommendation.annualConsumptionKwh, 'kWh')}` : ''}</div>
            {baselines.evidenceAnnualCostEur != null && <div className="text-slate-500">Belegpreis: {formatEuro(baselines.evidenceAnnualCostEur)} / Jahr</div>}
            {marketBest && <div className="mt-1 font-medium text-emerald-800 dark:text-emerald-300">Markt bestgeeignet: {formatEuro(marketBest.year1Eur)} / Jahr ({marketBest.provider})</div>}
            {recommendation?.deltaYear1Eur != null && <div className="mt-1 font-medium text-slate-800 dark:text-slate-100">Delta Jahr 1: {formatEuro(recommendation.deltaYear1Eur)} · Jahr 2: {recommendation?.deltaYear2Eur != null ? formatEuro(recommendation.deltaYear2Eur) : '–'}</div>}
            {marketBest && <div className="mt-1 text-slate-500">{marketBest.availability === 'source_claimed' ? 'Angebotsquelle: Vergleichsportal – PLZ-Gültigkeit vor Abschluss prüfen' : marketBest.availability === 'unverified' ? 'Angebotspreis ohne PLZ-Nachweis – nur Anhaltspunkt' : 'Verfügbarkeit verifiziert'}</div>}
            <div className="mt-1 text-slate-500">Entscheidungsbasis = aktueller Vertrag − bestgeeignetes Angebot. Keine Anbieteranfrage, keine Ausführung.</div>
          </div>
        ) : recommendation?.annualCostEstimateEur != null ? (
          <div className="mt-2 max-w-[250px] text-xs leading-5 text-slate-600 dark:text-slate-300"><div className="font-medium text-slate-800 dark:text-slate-100">{formatEuro(recommendation.annualCostEstimateEur)} / Jahr{recommendation?.annualConsumptionKwh ? ` · ${formatNumber(recommendation.annualConsumptionKwh, 'kWh')}` : ''}</div><div className="mt-1 text-slate-500">Vergleichsbasis aus Primärbeleg. Keine Anbieteranfrage, keine Ausführung.</div></div>
        ) : null}
      </td>
     <td className="px-5 py-4 align-top">{quarterly ? <><Badge variant="outline" className={`gap-1 ${quarterly.className}`}><QuarterlyIcon className="h-3.5 w-3.5" />{quarterly.label}</Badge><div className="mt-2 max-w-[250px] text-xs leading-5 text-slate-600 dark:text-slate-300">Zyklus {quarterlyReview.cycle} · rein interne Prüfung</div><div className="mt-1 text-xs text-slate-500">Keine Anbieteranfrage und keine Tarifausführung.</div></> : <span className="text-xs text-slate-500">Keine Quartalsprüfung offen</span>}</td>
      <td className="max-w-xs px-5 py-4 align-top text-sm leading-5 text-slate-700 dark:text-slate-200">{record.nextAction}{record.supplyScope === 'individual_supply_point' && <div className="mt-2 text-xs font-medium text-slate-500">Nicht aggregieren und nicht auf Einheiten verteilen.</div>}</td>
    </tr>
  );
}
